import { Show } from "@refinedev/antd";
import { CanAccess, useCan, useNotification, useOne } from "@refinedev/core";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Empty,
  Form,
  Image,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tabs,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { api } from "../api";
import { ReasonPicker } from "../components/reason-picker";
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT,
  DEACTIVATE_REASONS,
  DEGREES,
  DOCUMENT_TYPES,
  GENDERS,
  TUTOR_REJECT_REASONS,
  VERIFICATION_STATUS,
} from "../labels";
import type { TutorDetail } from "../types";
import { TutorFinanceTab } from "./tutor-finance-tab";
import { TutorLessonsTab } from "./tutor-lessons-tab";

const sizeLabel = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.ceil(bytes / 1024)} KB`;

/**
 * /tutors/:id: everything the decision needs. Files open from S3 with links that expire in a
 * few minutes; the page reloads them when it is focused again.
 */
export function TutorShowPage() {
  const { id = "" } = useParams();
  const { open } = useNotification();
  const { result: tutor, query } = useOne<TutorDetail>({ resource: "tutors", id });
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: canSeeLessons } = useCan({ resource: "cancellation-requests", action: "list" });
  const { data: canSeeFinance } = useCan({ resource: "payouts", action: "list" });
  const tab = searchParams.get("tab") ?? "profile";
  // The decision that needs a written reason: rejecting an application or making a tutor passive.
  const [noteFor, setNoteFor] = useState<"REJECT" | "DEACTIVATE" | null>(null);
  const [busy, setBusy] = useState(false);
  const [approving, setApproving] = useState(false);
  const [useApplicationVideo, setUseApplicationVideo] = useState(false);
  const [form] = Form.useForm<{ note: string }>();

  async function decide(
    body:
      | { decision: "APPROVE"; useApplicationVideo?: boolean }
      | { decision: "REOPEN" | "REACTIVATE" }
      | { decision: "REJECT" | "DEACTIVATE"; note: string },
  ) {
    setBusy(true);
    try {
      await api(`/api/office/tutors/${id}/decision`, { method: "POST", body });
      open?.({
        type: "success",
        message: {
          APPROVE: "Başvuru onaylandı.",
          REJECT: "Başvuru reddedildi.",
          REOPEN: "Başvuru tekrar incelemeye alındı.",
          DEACTIVATE: "Eğitmen pasife alındı.",
          REACTIVATE: "Eğitmen tekrar aktif edildi.",
        }[body.decision],
      });
      setNoteFor(null);
      setApproving(false);
      form.resetFields();
      await query.refetch();
    } catch (error) {
      open?.({ type: "error", message: error instanceof Error ? error.message : "Hata oluştu." });
    } finally {
      setBusy(false);
    }
  }

  const status = tutor ? VERIFICATION_STATUS[tutor.verificationStatus] : undefined;

  return (
    <Show
      title={tutor?.name ?? "Eğitmen"}
      isLoading={query.isLoading}
      headerButtons={
        tutor?.verificationStatus === "PENDING" ? (
          <CanAccess resource="tutors" action="verify">
            <Space>
              <Button danger onClick={() => setNoteFor("REJECT")}>
                Reddet
              </Button>
              <Button type="primary" loading={busy} onClick={() => setApproving(true)}>
                Onayla
              </Button>
            </Space>
          </CanAccess>
        ) : tutor?.verificationStatus === "REJECTED" ? (
          <CanAccess resource="tutors" action="verify">
            <Popconfirm
              title="Başvuru tekrar incelemeye alınsın mı?"
              description="Durumu İncelemede olur; sonra onaylanabilir veya reddedilebilir."
              okText="İncelemeye al"
              cancelText="Vazgeç"
              onConfirm={() => decide({ decision: "REOPEN" })}
            >
              <Button loading={busy}>Tekrar incelemeye al</Button>
            </Popconfirm>
          </CanAccess>
        ) : tutor?.verificationStatus === "VERIFIED" ? (
          // Product decision 2026-10-13: an approved tutor can be made passive (not bookable,
          // panel closed) once no lessons are left to hold, and active again.
          <CanAccess resource="tutors" action="verify">
            <Button danger onClick={() => setNoteFor("DEACTIVATE")}>
              Pasife al
            </Button>
          </CanAccess>
        ) : tutor?.verificationStatus === "SUSPENDED" ? (
          <CanAccess resource="tutors" action="verify">
            <Popconfirm
              title="Eğitmen tekrar aktif edilsin mi?"
              description="Profili öğrencilere görünür ve eğitmen paneli açılır."
              okText="Aktif et"
              cancelText="Vazgeç"
              onConfirm={() => decide({ decision: "REACTIVATE" })}
            >
              <Button type="primary" loading={busy}>
                Tekrar aktif et
              </Button>
            </Popconfirm>
          </CanAccess>
        ) : null
      }
    >
      {tutor ? (
        <Tabs
          activeKey={tab}
          onChange={(key) =>
            setSearchParams(key === "profile" ? {} : { tab: key }, { replace: true })
          }
          items={[
            {
              key: "profile",
              label: "Profil",
              children: (
                <Row gutter={[24, 24]}>
                  <Col xs={24} lg={14}>
                    <Space direction="vertical" size={24} style={{ width: "100%" }}>
                      <Descriptions column={1} bordered size="small">
                        <Descriptions.Item label="Durum">
                          <Tag color={status?.color}>{status?.label}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="Başvuru">
                          {tutor.onboardingSubmittedAt
                            ? dayjs(tutor.onboardingSubmittedAt).format(DATE_TIME_FORMAT)
                            : "Gönderilmedi"}
                        </Descriptions.Item>
                        <Descriptions.Item label="E-posta">
                          {tutor.email}{" "}
                          {tutor.emailVerified ? (
                            <Tag color="green">Doğrulandı</Tag>
                          ) : (
                            <Tag>Doğrulanmadı</Tag>
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Telefon">
                          {tutor.phoneNumber ?? "—"}{" "}
                          {tutor.phoneNumber ? (
                            tutor.phoneNumberVerified ? (
                              <Tag color="green">Doğrulandı</Tag>
                            ) : (
                              <Tag>Doğrulanmadı</Tag>
                            )
                          ) : null}
                        </Descriptions.Item>
                        <Descriptions.Item label="Doğum tarihi">
                          {tutor.identity
                            ? dayjs(tutor.identity.birthDate).format(DATE_FORMAT)
                            : "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Cinsiyet">
                          {tutor.identity
                            ? (GENDERS[tutor.identity.gender] ?? tutor.identity.gender)
                            : "—"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Sözleşme">
                          {tutor.agreement
                            ? `${tutor.agreement.documentVersion} · ${dayjs(tutor.agreement.acceptedAt).format(DATE_TIME_FORMAT)}`
                            : "Kabul edilmedi"}
                        </Descriptions.Item>
                      </Descriptions>

                      <Card size="small" title="Belgeler">
                        {tutor.documents.length > 0 ? (
                          <List
                            dataSource={tutor.documents}
                            renderItem={(document) => (
                              <List.Item
                                actions={[
                                  <a
                                    key="open"
                                    href={document.url}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    Aç
                                  </a>,
                                ]}
                              >
                                <List.Item.Meta
                                  title={DOCUMENT_TYPES[document.type] ?? document.type}
                                  description={`${document.fileName} · ${sizeLabel(document.sizeBytes)} · ${dayjs(document.uploadedAt).format(DATE_TIME_FORMAT)}`}
                                />
                              </List.Item>
                            )}
                          />
                        ) : (
                          <Empty description="Belge yüklenmedi" />
                        )}
                      </Card>

                      <Card size="small" title="Eğitim ve dersler">
                        {tutor.educations.map((education) => (
                          <Typography.Paragraph
                            key={`${education.university}-${education.department}`}
                            style={{ marginBottom: 8 }}
                          >
                            {education.university} · {education.department} (
                            {DEGREES[education.degree] ?? education.degree}
                            {education.graduationYear ? `, ${education.graduationYear}` : ""})
                          </Typography.Paragraph>
                        ))}
                        <Space wrap>
                          {tutor.subjects.map((subject) => (
                            <Tag key={subject}>{subject}</Tag>
                          ))}
                        </Space>
                      </Card>

                      {tutor.suspension ? (
                        <Card size="small" title="Pasif nedeni">
                          <Typography.Paragraph style={{ whiteSpace: "pre-wrap" }}>
                            {tutor.suspension.note || "—"}
                          </Typography.Paragraph>
                          <Typography.Text type="secondary">
                            {tutor.suspension.by ?? "Sunucu komutu"} ·{" "}
                            {dayjs(tutor.suspension.at).format(DATE_TIME_FORMAT)}
                          </Typography.Text>
                        </Card>
                      ) : null}
                    </Space>
                  </Col>
                  <Col xs={24} lg={10}>
                    <Space direction="vertical" size={24} style={{ width: "100%" }}>
                      <Card size="small" title="Profil fotoğrafı">
                        {tutor.photoUrl ? (
                          <Image src={tutor.photoUrl} alt={tutor.name} width={200} />
                        ) : (
                          <Empty description="Fotoğraf yok" />
                        )}
                      </Card>
                      <Card size="small" title="Başvuru videosu (sadece ekip görür)">
                        {tutor.applicationVideoUrl ? (
                          <video
                            src={tutor.applicationVideoUrl}
                            controls
                            style={{ width: "100%", maxHeight: 320 }}
                          />
                        ) : (
                          <Empty description="Video yok" />
                        )}
                      </Card>
                      <Card size="small" title="Profil videosu (öğrenciler görür)">
                        {tutor.videoUrl ? (
                          <video
                            src={tutor.videoUrl}
                            controls
                            style={{ width: "100%", maxHeight: 320 }}
                          />
                        ) : (
                          <Empty description="Video yok" />
                        )}
                      </Card>
                      <Card size="small" title="Hakkımda">
                        <Typography.Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                          {tutor.bio || "—"}
                        </Typography.Paragraph>
                      </Card>
                    </Space>
                  </Col>
                </Row>
              ),
            },
            ...(canSeeLessons?.can
              ? [
                  {
                    key: "lessons",
                    label: "Dersler",
                    children: <TutorLessonsTab tutorId={tutor.id} />,
                  },
                ]
              : []),
            ...(canSeeFinance?.can
              ? [
                  {
                    key: "finance",
                    label: "Finans",
                    children: <TutorFinanceTab tutorId={tutor.id} />,
                  },
                ]
              : []),
          ]}
        />
      ) : null}

      <Modal
        title="Başvuruyu onayla"
        open={approving}
        okText="Onayla"
        cancelText="Vazgeç"
        confirmLoading={busy}
        onOk={() => decide({ decision: "APPROVE", useApplicationVideo })}
        onCancel={() => setApproving(false)}
      >
        <Typography.Paragraph type="secondary">
          Eğitmen paneli açılır ve profili öğrencilere görünür. Eğitmen, öğrencilere kendini
          tanıttığı bir profil videosu ekler; o da onaya düşer.
        </Typography.Paragraph>
        <Checkbox
          checked={useApplicationVideo}
          disabled={!tutor?.applicationVideoUrl}
          onChange={(event) => setUseApplicationVideo(event.target.checked)}
        >
          Başvuru videosu öğrencilere uygun; profil videosu olarak da kullanılsın
        </Checkbox>
      </Modal>

      <Modal
        title={noteFor === "DEACTIVATE" ? "Eğitmeni pasife al" : "Başvuruyu reddet"}
        open={noteFor !== null}
        okText={noteFor === "DEACTIVATE" ? "Pasife al" : "Reddet"}
        okButtonProps={{ danger: true }}
        cancelText="Vazgeç"
        confirmLoading={busy}
        onOk={() => form.submit()}
        onCancel={() => setNoteFor(null)}
      >
        <Typography.Paragraph type="secondary">
          {noteFor === "DEACTIVATE"
            ? "Profili öğrencilere görünmez, yeni ders alınamaz ve eğitmen paneli kapanır; eğitmen hesabının pasif olduğunu görür. Yaklaşan dersi varsa önce dersler yapılmalı ya da iptal edilmeli. Gerekçe Kayıtlar'da durur; sonra tekrar aktif edilebilir."
            : "Ret ekip içinde kalır: eğitmen başvurusunu incelemede görmeye devam eder ve bizden haber bekler. Gerekçe Kayıtlar'da durur; başvuru sonra tekrar incelemeye alınabilir."}
        </Typography.Paragraph>
        <ReasonPicker
          reasons={noteFor === "DEACTIVATE" ? DEACTIVATE_REASONS : TUTOR_REJECT_REASONS}
          onPick={(reason) => form.setFieldValue("note", reason)}
        />
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
          onFinish={({ note }) => noteFor && decide({ decision: noteFor, note })}
        >
          <Form.Item
            label="Gerekçe"
            name="note"
            rules={[
              {
                required: true,
                whitespace: true,
                min: 5,
                message: "Gerekçeyi yaz (en az 5 karakter).",
              },
              { max: 500, message: "Gerekçe en fazla 500 karakter olabilir." },
            ]}
          >
            <Input.TextArea rows={4} showCount maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </Show>
  );
}
